import {ChangeDetectionStrategy, Component, effect, EventEmitter, Injectable, input, OnDestroy, Output} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {InlineSkjemaHandlingerKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-handlinger/inline-skjema-handlinger";
import {InlineSkjemaKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema";
import {InlineSkjemaTittelKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-tittel/inline-skjema-tittel";
import {MatButton} from "@angular/material/button";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {NavigasjonforhindretFlashDirektiv} from "web-ng2/delt/direktiver/navigasjonforhindret-flash/navigasjonforhindret-flash";
import {TranslateModule} from "@ngx-translate/core";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {Base64DataUrl, BrukerId, BrukerModell, LitenIkkeTomStreng, OppdaterBrukerSpesifikasjonV2} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {BrukerDatatjeneste} from "felles-ng2/tjenester/datalager/BrukerDatatjeneste";
import {MatInput} from "@angular/material/input";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {AvatarKomponent} from "web-ng2/delt/komponenter/avatar/avatar";
import {BildeVelgerMedSkaleringKomponent} from "web-ng2/delt/komponenter/skjema/bilde-velger-med-skalering/bilde-velger-med-skalering";

const debug = initDebugger('BrukerOppdaterAvatarOgNavnKomponent');

/**
 * Rediger brukers avatar og navn
 */
@Component({
    selector: 'bruker-oppdater-avatar-og-navn',
    templateUrl: 'bruker-oppdater-avatar-og-navn.html',
    styleUrl: 'bruker-oppdater-avatar-og-navn.scss',
    host: {'data-testid': 'bruker-oppdater-avatar-og-navn'},
    imports: [
        FormsModule,
        InlineSkjemaHandlingerKomponent,
        InlineSkjemaKomponent,
        InlineSkjemaTittelKomponent,
        MatButton,
        MatFormField,
        MatLabel,
        NavigasjonforhindretFlashDirektiv,
        TranslateModule,
        VisSpinnerDirektiv,
        ReactiveFormsModule,
        MatError,
        MatInput,
        StandardValideringsfeilPipe,
        AvatarKomponent,
        BildeVelgerMedSkaleringKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BrukerOppdaterAvatarOgNavnKomponent implements OnDestroy {
    static ajsSelector = 'brukerOppdaterAvatarOgNavn';

    @Output() vedLagret = new EventEmitter<void>(); //output() fungerer ikke når komp brukt i ajs
    @Output() vedAvbrutt = new EventEmitter<void>(); //output() fungerer ikke når komp brukt i ajs

    bruker = input.required<BrukerModell>();

    lagrer = asynkArbeidPågårSignal();
    skjema = new FormGroup({
        avatar: new FormControl<Base64DataUrl | null>(null),
        navn: new FormControl<LitenIkkeTomStreng>('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng})
    });

    constructor(private avhengigheter: BrukerOppdaterAvatarOgNavnKomponentAvhengigheter, private navigasjonDeaktivering: NavigasjonDeaktivering) {
        navigasjonDeaktivering.deaktiverNavigasjon();
        effect(() => {
            this.skjema.patchValue({
                navn: this.bruker().navn!
            });
        });
    }

    async lagre() {
        await this.lagrer.utfør(async () => {
            const spesifikasjon = this.somSpesifikasjon();
            await this.avhengigheter.oppdater(this.bruker().id, spesifikasjon);
            this.vedLagret.emit();
        });
    }

    avbryt() {
        this.vedAvbrutt.emit();
    }

    ngOnDestroy(): void {
        this.navigasjonDeaktivering.reaktiverNavigasjon();
    }

    //private hjelpemetoder

    private somSpesifikasjon(): OppdaterBrukerSpesifikasjonV2 {
        const verdier = this.skjema.value;
        return {
            navn: verdier.navn!,
            avatarBase64: verdier.avatar ?? null,
        }
    }
}

@Injectable()
export class BrukerOppdaterAvatarOgNavnKomponentAvhengigheter {

    constructor(private brukerDatatjeneste: BrukerDatatjeneste) {
    }

    async oppdater(id: BrukerId, spesifikasjon: OppdaterBrukerSpesifikasjonV2): Promise<void> {
        await this.brukerDatatjeneste.oppdaterNavnOgAvatar(id, spesifikasjon);
    }
}
